import { memo, type ReactNode, useMemo } from 'react'

import { Table, TableBody } from '@/components/ui/table'
import { cn } from '@/lib/utils'

import type { DataTableSkeletonProps } from './data-table.interface'
import { DataTableSkeletonHeader } from './data-table-skeleton-header.atom'
import { DataTableSkeletonRow } from './data-table-skeleton-row.atom'

const generateSkeletonIdList = (prefix: string, count: number): string[] =>
  Array.from({ length: count }, (_, i) => `${prefix}-${i}`)

/**
 * Renders a skeleton placeholder for the data table while loading.
 */
const DataTableSkeletonBase: React.FC<DataTableSkeletonProps> = ({
  columnCount,
  rowCount = 10,
  hasCheckbox = false,
  hasActions = false,
  className,
}): ReactNode => {
  const columnIdList = useMemo(() => generateSkeletonIdList('col', columnCount), [columnCount])
  const rowIdList = useMemo(() => generateSkeletonIdList('row', rowCount), [rowCount])

  return (
    <div className={cn('data-table-skeleton rounded-md border', className)}>
      <Table>
        <DataTableSkeletonHeader
          columnIdList={columnIdList}
          hasCheckbox={hasCheckbox}
          hasActions={hasActions}
        />
        <TableBody>
          {rowIdList.map((rowId, rowIndex) => (
            <DataTableSkeletonRow
              key={rowId}
              rowId={rowId}
              rowIndex={rowIndex}
              columnIdList={columnIdList}
              hasCheckbox={hasCheckbox}
              hasActions={hasActions}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export const DataTableSkeleton = memo(DataTableSkeletonBase)

DataTableSkeleton.displayName = 'DataTableSkeleton'
